import React, { useEffect, useMemo, useState } from 'react'
import { Search, Loader2, Printer, FileDown } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { getPayrollComparison } from '@/services/payrollService'
import type { PayrollCompareLine, PayrollCompareResponse } from '@/api/types/payroll.types'
import type { Employee } from '@/api/types'
import { formatEmployeeDisplayName } from '@/lib/employeeName'
import { getLogoPath } from '@/components/shared/Logo'
import { formatCurrency } from '@/lib/format'

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const TOTAL_KEYS = new Set(['totalAllowance', 'grossPay', 'totalDeduction', 'netSalary'])

function getDefaultMonthYear(offset = 0): string {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() + offset)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function formatMonthYearLabel(ym?: string | null): string {
  if (!ym || ym.length < 6) return '—'
  const parts = String(ym).split('-')
  let monthNum: number
  let yearStr: string
  if (parts.length >= 2 && parts[0].length === 4) {
    yearStr = parts[0]
    monthNum = parseInt(parts[1], 10)
  } else if (parts.length >= 2 && parts[1].length === 4) {
    monthNum = parseInt(parts[0], 10)
    yearStr = parts[1]
  } else {
    return ym
  }
  if (monthNum >= 1 && monthNum <= 12) return `${MONTH_NAMES[monthNum - 1]} ${yearStr}`
  return ym
}

function money(n?: number | null): string {
  return formatCurrency(Number(n ?? 0))
}

export const PayrollCompareReport: React.FC = () => {
  const { t } = useTranslation('common')
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId

  const [employees, setEmployees] = useState<Employee[]>([])
  const [empId, setEmpId] = useState(0)
  const [month1, setMonth1] = useState(() => getDefaultMonthYear(-1))
  const [month2, setMonth2] = useState(() => getDefaultMonthYear(0))
  const [loading, setLoading] = useState(false)
  const [printLoading, setPrintLoading] = useState(false)
  const [result, setResult] = useState<PayrollCompareResponse | null>(null)

  useEffect(() => {
    if (!branchId) {
      setEmployees([])
      return
    }
    let cancelled = false
    employeesService
      .getAllEmployees(branchId, 1, 1000)
      .then((res) => {
        if (!cancelled) setEmployees(res?.data ?? [])
      })
      .catch(() => {
        if (!cancelled) setEmployees([])
      })
    return () => {
      cancelled = true
    }
  }, [branchId])

  const employeeOptions = useMemo(() => {
    const seen = new Set<number>()
    return employees
      .filter((emp) => {
        if (!emp.id || seen.has(emp.id)) return false
        seen.add(emp.id)
        return true
      })
      .map((emp) => ({
        value: String(emp.id),
        label: `${emp.empCode ? `${emp.empCode} — ` : ''}${formatEmployeeDisplayName(emp) || emp.empCode || `EMP-${emp.id}`}`,
      }))
  }, [employees])

  const lineLabel = (key: string) => t(`payrollComparePage.lines.${key}`, { defaultValue: key })

  const loadReport = async () => {
    if (!branchId) {
      toast.error(t('payrollComparePage.selectBranch'))
      return
    }
    if (!empId) {
      toast.error(t('payrollComparePage.selectEmployee'))
      return
    }
    if (!month1 || !month2) {
      toast.error(t('payrollComparePage.selectMonths'))
      return
    }
    if (month1 === month2) {
      toast.error(t('payrollComparePage.sameMonths'))
      return
    }

    setLoading(true)
    try {
      const data = await getPayrollComparison(branchId, empId, month1, month2)
      setResult(data)
      if (!data.payroll1Found || !data.payroll2Found) {
        const missing = [
          !data.payroll1Found ? formatMonthYearLabel(month1) : null,
          !data.payroll2Found ? formatMonthYearLabel(month2) : null,
        ].filter(Boolean)
        toast.error(t('payrollComparePage.missingPayroll', { months: missing.join(', ') }))
      }
    } catch (e: any) {
      setResult(null)
      toast.error(e?.response?.data?.message ?? e?.message ?? t('payrollComparePage.loadFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handlePrint = () => {
    if (!result) {
      toast.error(t('payrollComparePage.noDataPrint'))
      return
    }
    setPrintLoading(true)
    try {
      const branchName = user?.selectedBranch?.branchName ?? 'Branch'
      const dateStr = new Date().toLocaleDateString('en-US', { month: 'numeric', day: '2-digit', year: '2-digit' })
      const m1 = formatMonthYearLabel(month1)
      const m2 = formatMonthYearLabel(month2)
      const primaryColor = 'hsl(225, 46%, 16%)'
      const thStyle = `background:${primaryColor};color:white;padding:6px 8px;text-align:left;font-size:11px;font-weight:600;border:1px solid #e5e7eb;`
      const tdStyle = 'padding:6px 8px;font-size:11px;border:1px solid #e5e7eb;'
      const rows = (result.lines ?? [])
        .map(
          (line: PayrollCompareLine) => `
        <tr>
          <td style="${tdStyle}">${lineLabel(line.key)}</td>
          <td style="${tdStyle}text-align:right">${money(line.amount1)}</td>
          <td style="${tdStyle}text-align:right">${money(line.amount2)}</td>
          <td style="${tdStyle}text-align:right">${money(line.difference)}</td>
        </tr>`,
        )
        .join('')
      const html = `<!DOCTYPE html><html><head><title>Payroll Comparison</title></head><body style="font-family:Arial,sans-serif;padding:16px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <img src="${getLogoPath()}" style="height:40px" />
          <div style="text-align:right;font-size:12px">${branchName}<br/>${dateStr}</div>
        </div>
        <h2 style="margin:0 0 8px;font-size:16px">${t('navigation.payrollCompareReport')}</h2>
        <p style="font-size:12px;margin:0 0 12px">${result.empCode ?? ''} ${result.employeeName ?? ''} &nbsp;|&nbsp; ${m1} vs ${m2}</p>
        <table style="width:100%;border-collapse:collapse">
          <thead><tr>
            <th style="${thStyle}">${t('payrollComparePage.colComponent')}</th>
            <th style="${thStyle}">${m1}</th>
            <th style="${thStyle}">${m2}</th>
            <th style="${thStyle}">${t('payrollComparePage.colDifference')}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body></html>`
      const win = window.open('', '_blank')
      if (!win) return
      win.document.write(html)
      win.document.close()
      win.focus()
      setTimeout(() => {
        win.print()
        win.close()
      }, 300)
    } finally {
      setPrintLoading(false)
    }
  }

  const handleCsv = () => {
    if (!result?.lines?.length) {
      toast.error(t('payrollComparePage.noDataExport'))
      return
    }
    const m1 = formatMonthYearLabel(month1)
    const m2 = formatMonthYearLabel(month2)
    const headers = [t('payrollComparePage.colComponent'), m1, m2, t('payrollComparePage.colDifference')].join(',')
    const rows = result.lines.map((line) =>
      [`"${lineLabel(line.key)}"`, money(line.amount1), money(line.amount2), money(line.difference)].join(','),
    )
    const blob = new Blob([[headers, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `payroll-compare-${result.empCode || empId}-${month1}_${month2}.csv`
    link.click()
  }

  const diffClass = (value: number) => {
    if (value > 0) return 'text-emerald-600'
    if (value < 0) return 'text-rose-600'
    return 'text-muted-foreground'
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('navigation.payrollCompareReport')}
        description={t('payrollComparePage.description')}
      />

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>{t('payrollComparePage.employee')}</Label>
              <SearchableSelect
                options={employeeOptions}
                value={empId ? String(empId) : ''}
                onValueChange={(v) => setEmpId(v ? Number(v) : 0)}
                placeholder={t('payrollComparePage.selectEmployeePlaceholder')}
                searchPlaceholder={t('payrollComparePage.searchEmployee')}
                emptyMessage={t('payrollComparePage.emptyEmployee')}
                disabled={!branchId}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('payrollComparePage.payroll1')}</Label>
              <Input type="month" value={month1} onChange={(e) => setMonth1(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t('payrollComparePage.payroll2')}</Label>
              <Input type="month" value={month2} onChange={(e) => setMonth2(e.target.value)} />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={() => void loadReport()} disabled={loading || !branchId}>
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                {t('payrollComparePage.search')}
              </Button>
              <Button variant="outline" onClick={handlePrint} disabled={printLoading || !result}>
                {printLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
                {t('payrollComparePage.print')}
              </Button>
              <Button variant="outline" onClick={handleCsv} disabled={!result}>
                <FileDown className="w-4 h-4 mr-2" />
                {t('payrollComparePage.csv')}
              </Button>
            </div>
          </div>

          {result && (
            <p className="text-sm text-muted-foreground">
              {result.empCode ? `${result.empCode} — ` : ''}
              {result.employeeName || '—'}
            </p>
          )}

          <div className="rounded-md border overflow-x-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : !result ? (
              <div className="py-12 text-center text-muted-foreground">
                {t('payrollComparePage.emptyState')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('payrollComparePage.colComponent')}</TableHead>
                    <TableHead className="text-right">{formatMonthYearLabel(month1)}</TableHead>
                    <TableHead className="text-right">{formatMonthYearLabel(month2)}</TableHead>
                    <TableHead className="text-right">{t('payrollComparePage.colDifference')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(result.lines ?? []).map((line) => (
                    <TableRow key={line.key} className={TOTAL_KEYS.has(line.key) ? 'font-semibold bg-muted/40' : ''}>
                      <TableCell>{lineLabel(line.key)}</TableCell>
                      <TableCell className="text-right">{money(line.amount1)}</TableCell>
                      <TableCell className="text-right">{money(line.amount2)}</TableCell>
                      <TableCell className={`text-right ${diffClass(line.difference)}`}>
                        {line.difference > 0 ? '+' : ''}
                        {money(line.difference)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
